import React, { useEffect, useState, useContext } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import { fetchDomeById, deleteDome } from "../../api/dome.api"; // <-- imported deleteDome

const DomeDetails = () => {
  const { background } = useContext(ThemeContext);
  const isDark = background === "dark";

  const { domeid: id } = useParams(); // dome id from route
  const navigate = useNavigate();

  const [dome, setDome] = useState(null);
  const [mainImage, setMainImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    const loadDome = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const response = await fetchDomeById(id);
        console.log("🚀 ~ loadDome ~ response:", response);
        const data = response?.data || response;
        setDome(data);
        const firstImg =
          (data?.images && data.images.length > 0 && data.images[0]) || null;
        setMainImage(firstImg);
      } catch (err) {
        console.error("Error fetching dome:", err);
        setError(
          err?.response?.data?.message || err?.message || "Failed to load dome"
        );
      } finally {
        setLoading(false);
      }
    };

    loadDome();
  }, [id]);

  const navigateToSession = () => {
    if (!dome?._id) {
      console.error("Dome data is missing!");
      return;
    }

    navigate("sessions", {
      state: { domeData: dome },
    });
  };

  const navigateToBookings = () => {
    if (!dome?._id) {
      console.error("Dome data is missing!");
      return;
    }

    navigate("bookings", {
      state: { domeData: dome },
    });
  };

  const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : "N/A");
  const formatAddress = (domeLocation) => domeLocation || "N/A";

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!dome?._id) return;
    setDeleting(true);
    setDeleteError(null);

    try {
      await deleteDome(dome._id);
      setShowDeleteModal(false); // close only on success
      navigate(-1); // go back after successful deletion
    } catch (err) {
      console.error("Delete failed:", err);
      setDeleteError(
        err?.response?.data?.message || err?.message || "Failed to delete dome"
      );
      // keep modal open so user sees the error
    } finally {
      setDeleting(false);
    }
  };

  // Close modal via Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setShowDeleteModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger mb-3">Error Loading Dome</h4>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn btn-primary">
          Go Back
        </button>
      </div>
    );

  if (!dome)
    return (
      <div className="container py-5 text-center">
        <h4>No dome data found</h4>
        <button onClick={() => navigate(-1)} className="btn btn-primary">
          Go Back
        </button>
      </div>
    );

  // dome fields (safe defaults)
  const {
    domeName,
    domeId,
    domeDescription,
    pricePerSession,
    offerPercent,
    location,
    images = [],
    hostRef,
    createdAt,
    updatedAt,
    archived,
    _id,
  } = dome;

  const hostDisplayName =
    hostRef?.companyDisplayName ||
    hostRef?.companyName ||
    hostRef?.hostDisplayName ||
    hostRef?.hostName;

  return (
    <div style={{ minHeight: "100vh" }}>
      <div className="container-fluid py-3">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h2 className="mb-0">Dome Details</h2>
          <div>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline-danger me-2"
            >
              <i className="fas fa-arrow-left me-2"></i>Back
            </button>
            {/* {hostRef?._id && (
              <button
                onClick={() =>
                  navigate(`/host/${hostRef._id}`, {
                    state: { hostData: hostRef },
                  })
                }
                className="btn btn-outline-secondary me-2"
              >
                <i className="fas fa-building me-1"></i>View Host
              </button>
            )} */}
            {/* <button onClick={navigateToSession} className="btn btn-success">
              <i className="fas fa-eye me-2"></i>View Timeslots
            </button>
            <button
              onClick={navigateToBookings}
              className="btn btn-warning ms-1"
            >
              <i className="fas fa-eye me-2"></i>View Bookings
            </button> */}
          </div>
        </div>

        {/* Cover Image */}
        {dome?.coverImage && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">Cover Image</h6>
            <img
              src={dome?.coverImage}
              alt="Cover"
              className="img-fluid w-100 rounded"
              style={{ maxHeight: 220, objectFit: "cover" }}
            />
          </div>
        )}

        {/* Info Card with image + summary */}
        <div className={`card mb-4 ${isDark ? "bg-dark text-white" : ""}`}>
          <div className="row g-0">
            {/* Image column */}
            <div className="col-md-5">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={domeName || domeId || "Dome image"}
                  className="img-fluid w-100"
                  style={{
                    height: 320,
                    objectFit: "cover",
                    borderRadius: "4px 0 0 4px",
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center bg-secondary"
                  style={{ height: 320, color: "white" }}
                >
                  <i className="fas fa-cube fa-3x"></i>
                </div>
              )}
              {/* Thumbnails */}
              {images.length > 0 && (
                <div
                  className="d-flex gap-2 p-3 overflow-auto"
                  style={{ background: isDark ? "#111" : "#fafafa" }}
                >
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMainImage(img);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      style={{
                        width: 80,
                        height: 60,
                        borderRadius: 6,
                        overflow: "hidden",
                        cursor: "pointer",
                        border:
                          mainImage === img
                            ? "2px solid #d63384"
                            : "1px solid rgba(0,0,0,0.08)",
                        flex: "0 0 auto",
                      }}
                    >
                      <img
                        src={img}
                        alt={`thumb-${idx}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Details column */}
            <div className="col-md-7">
              <div className="card-body d-flex flex-column h-100">
                {/* Spacer to push footer to bottom */}
                <div className="p-3">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => {
                        setDeleteError(null);
                        setShowDeleteModal(true);
                      }}
                      disabled={deleting}
                    >
                      <i className="fas fa-trash me-1"></i> Delete
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate(`edit`, { state: { dome } })}
                    >
                      <i className="fas fa-edit me-1"></i> Edit
                    </button>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className="mb-1">{domeName || "Untitled Dome"}</h3>
                    <p className="text-muted mb-1">
                      {domeId && <span>ID: {domeId}</span>}
                    </p>
                    <p className="mb-2 text-muted">
                      {hostDisplayName ? `Hosted by: ${hostDisplayName}` : null}
                    </p>
                  </div>

                  <div className="text-end">
                    <div>
                      <h4
                        className="mb-0"
                        style={{ color: isDark ? "#fff" : "#111" }}
                      >
                        {typeof pricePerSession === "number"
                          ? `$${pricePerSession}`
                          : pricePerSession || "$0"}
                      </h4>
                      <small className="text-muted">
                        Offer: {offerPercent ?? 0}%
                      </small>
                    </div>
                  </div>
                </div>

                <hr />

                <div
                  className="mb-3"
                  style={{ maxHeight: 140, overflowY: "auto" }}
                >
                  <h6 className="mb-1">Description</h6>
                  <p className="mb-0 text-muted">
                    {domeDescription || "No description provided."}
                  </p>
                </div>

                <div className="row mt-3">
                  <div className="col-6">
                    <p className="mb-1">
                      <strong>Location</strong>
                    </p>
                    <p className="text-muted mb-0">{formatAddress(location)}</p>
                  </div>
                  <div className="col-6">
                    <p className="mb-1">
                      <strong>Created</strong>
                    </p>
                    <p className="text-muted mb-0">
                      {formatDateTime(createdAt)}
                    </p>
                  </div>
                </div>

                <div className="row mt-2">
                  <div className="col-6">
                    <p className="mb-1">
                      <strong>Last Updated</strong>
                    </p>
                    <p className="text-muted mb-0">
                      {formatDateTime(updatedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional panels (unchanged) */}
        <div className="row">
          <div className="col-lg-6 mb-4">
            <div className={`card h-100 ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Location</h4>
              </div>
              <div className="card-body">
                <p className="mb-1">
                  <strong>Address / Area:</strong>
                </p>
                <p className="text-muted">{location || "N/A"}</p>
                {dome.latitude && dome.longitude && (
                  <p className="mb-0">
                    <strong>Coordinates:</strong> {dome.latitude},{" "}
                    {dome.longitude}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className={`card h-100 ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0 d-flex justify-content-between align-items-center">
                <h4 className="card-title mb-0">Images</h4>
                <small className="text-muted">{images.length} image(s)</small>
              </div>
              <div className="card-body">
                {images.length === 0 ? (
                  <div className="text-muted">No images uploaded</div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {images.map((img, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: 120,
                          height: 80,
                          overflow: "hidden",
                          borderRadius: 6,
                        }}
                      >
                        <img
                          src={img}
                          alt={`dome-img-${idx}`}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setMainImage(img);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Raw / Extra Properties */}
        <div className="row">
          <div className="col-12 mb-4">
            <div className={`card ${isDark ? "bg-dark" : ""}`}>
              <div className="card-header border-0">
                <h4 className="card-title">Other Properties</h4>
              </div>
              <div
                className="card-body"
                style={{ maxHeight: 240, overflowY: "auto" }}
              >
                <table className="table table-borderless mb-0">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "Dome ID", value: domeId },
                      { key: "Price", value: pricePerSession ? `$${pricePerSession}` : "$0" },
                      // { key: "Offer %", value: `${offerPercent ?? 0}%` },
                      { key: "Location", value: location },
                      { key: "Host", value: hostDisplayName || "N/A" },
                      { key: "Created At", value: formatDateTime(createdAt) },
                      { key: "Updated At", value: formatDateTime(updatedAt) },
                      // { key: "Archived", value: archived ? "Yes" : "No" },
                    ].map(({ key, value }) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{value ?? "N/A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal — rendered via portal at document.body to avoid parent CSS interference */}
      {showDeleteModal &&
        ReactDOM.createPortal(
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.55)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            }}
            onClick={() => {
              if (!deleting) setShowDeleteModal(false);
            }}
          >
            <div
              className={`p-4 ${isDark ? "text-white" : "text-dark"}`}
              style={{
                width: 500,
                maxWidth: "100%",
                backgroundColor: isDark ? "#1e1e2e" : "#ffffff",
                borderRadius: "15px",
                boxShadow: "0 15px 35px rgba(0,0,0,0.3)",
                border: isDark ? "1px solid #333" : "1px solid #eee",
                position: "relative"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h5 className="mb-2">Confirm Delete</h5>
              <p className="mb-3 text-muted">
                Are you sure you want to permanently delete the dome{" "}
                <strong>{domeName || domeId}</strong>? This action cannot be
                undone.
              </p>

              {deleteError && (
                <div className="alert alert-danger py-2">{deleteError}</div>
              )}

              <div className="d-flex justify-content-end gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DomeDetails;
